import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Loader2, Star, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";

const WHAT_WORKED_OPTIONS = [
  "Goût & saveurs",
  "Rapidité",
  "Simplicité",
  "Ingrédients accessibles",
  "Texture",
  "Originalité",
] as const;

const WHAT_DIDNT_OPTIONS = [
  "Trop long",
  "Trop compliqué",
  "Ingrédients introuvables",
  "Manque de goût",
  "Trop salé / épicé",
  "Instructions peu claires",
] as const;

interface FeedbackApiResponse {
  id: string;
  recipe_slug: string;
  ratings: Record<string, number>;
  notes: string | null;
  created_at: string;
}

interface FeedbackSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipeSlug: string;
  recipeTitle?: string;
}

export function FeedbackSheet({
  open,
  onOpenChange,
  recipeSlug,
  recipeTitle,
}: FeedbackSheetProps) {
  const [overallRating, setOverallRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [whatWorked, setWhatWorked] = useState<Set<string>>(new Set());
  const [whatDidnt, setWhatDidnt] = useState<Set<string>>(new Set());
  const [cookAgain, setCookAgain] = useState<boolean | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const toggleTag = (set: Set<string>, value: string): Set<string> => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
  };

  const handleSubmit = async () => {
    if (overallRating === 0) {
      toast.error("Veuillez donner une note globale");
      return;
    }

    setSubmitting(true);
    try {
      await api<FeedbackApiResponse>("/api/v1/feedback", {
        method: "POST",
        body: {
          recipe_slug: recipeSlug,
          ratings: {
            overall: overallRating,
            ...(cookAgain !== null ? { cook_again: cookAgain ? 1 : 0 } : {}),
          },
          notes:
            [
              whatWorked.size > 0 ? `✅ ${[...whatWorked].join(", ")}` : "",
              whatDidnt.size > 0 ? `❌ ${[...whatDidnt].join(", ")}` : "",
            ]
              .filter(Boolean)
              .join(" · ") || null,
        },
      });
      toast.success("Merci pour votre avis !");
      onOpenChange(false);
      resetForm();
    } catch {
      toast.error("Erreur lors de l'envoi — veuillez réessayer");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    onOpenChange(false);
    resetForm();
  };

  const resetForm = () => {
    setOverallRating(0);
    setHoverRating(0);
    setWhatWorked(new Set());
    setWhatDidnt(new Set());
    setCookAgain(null);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 mx-auto max-w-md border-t border-hair bg-background pb-[env(safe-area-inset-bottom)] shadow-soft-warm-lg data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-4 sm:bottom-auto sm:top-1/2 sm:max-w-lg sm:-translate-y-1/2 sm:border sm:rounded-none">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-hair px-6 py-4">
            <Dialog.Title className="font-serif text-[15px] font-medium">
              {recipeTitle ? `Ton avis sur ${recipeTitle}` : "Donne ton avis"}
            </Dialog.Title>
            <button
              aria-label="Fermer"
              onClick={handleSkip}
              className="text-foreground/60 hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          </div>

          <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
            {/* 1. Overall rating — stars */}
            <div className="mb-6">
              <p className="mb-2 text-[13px] font-medium text-foreground">
                Note globale
              </p>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    aria-label={`${star} étoile${star > 1 ? "s" : ""}`}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setOverallRating(star)}
                    className="p-0.5 transition-transform hover:scale-110"
                  >
                    <Star
                      className={cn(
                        "size-7 transition-colors",
                        (hoverRating || overallRating) >= star
                          ? "fill-accent text-accent"
                          : "fill-none text-ink-30",
                      )}
                    />
                  </button>
                ))}
              </div>
              {overallRating > 0 && (
                <p className="mt-1 text-[12px] text-ink-50">
                  {overallRating === 5 && "Excellent !"}
                  {overallRating === 4 && "Très bien"}
                  {overallRating === 3 && "Bien"}
                  {overallRating === 2 && "Passable"}
                  {overallRating === 1 && "Décevant"}
                </p>
              )}
            </div>

            {/* 2. What worked — tag pills */}
            <div className="mb-6">
              <p className="mb-2 text-[13px] font-medium text-foreground">
                Ce qui a fonctionné
              </p>
              <div className="flex flex-wrap gap-2">
                {WHAT_WORKED_OPTIONS.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() =>
                      setWhatWorked((prev) => toggleTag(prev, tag))
                    }
                    className={cn(
                      "rounded-pill border px-3.5 py-1.5 text-[12px] transition-colors",
                      whatWorked.has(tag)
                        ? "border-accent bg-accent/10 text-accent"
                        : "border-hair text-ink-60 hover:border-ink-30",
                    )}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* 3. What didn't — tag pills */}
            <div className="mb-6">
              <p className="mb-2 text-[13px] font-medium text-foreground">
                Ce qui n'a pas fonctionné
              </p>
              <div className="flex flex-wrap gap-2">
                {WHAT_DIDNT_OPTIONS.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setWhatDidnt((prev) => toggleTag(prev, tag))}
                    className={cn(
                      "rounded-pill border px-3.5 py-1.5 text-[12px] transition-colors",
                      whatDidnt.has(tag)
                        ? "border-destructive bg-destructive/10 text-destructive"
                        : "border-hair text-ink-60 hover:border-ink-30",
                    )}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* 4. Cook again — yes/no */}
            <div className="mb-4">
              <p className="mb-2 text-[13px] font-medium text-foreground">
                Tu referais ce plat ?
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={cookAgain === true ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCookAgain(true)}
                >
                  Oui
                </Button>
                <Button
                  type="button"
                  variant={cookAgain === false ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCookAgain(false)}
                >
                  Non
                </Button>
              </div>
            </div>
          </div>

          {/* Footer actions */}
          <div className="flex items-center gap-3 border-t border-hair px-6 py-4">
            <Button
              variant="ghost"
              size="sm"
              className="flex-1"
              onClick={handleSkip}
              disabled={submitting}
            >
              Passer
            </Button>
            <Button
              size="sm"
              className="flex-1"
              onClick={handleSubmit}
              disabled={submitting || overallRating === 0}
            >
              {submitting ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                "Envoyer"
              )}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
